import cytoscape from 'cytoscape';

type CytoscapeStyle = cytoscape.StylesheetJson;

const toArray = (maybe: unknown): CytoscapeStyle => {
  if (!maybe) return [];
  if (Array.isArray(maybe)) return maybe as CytoscapeStyle;
  if (typeof maybe === 'string') {
    try {
      const parsed = JSON.parse(maybe);
      return Array.isArray(parsed) ? (parsed as CytoscapeStyle) : [];
    } catch {
      // Unsupported string format (CSS). Ignored for typed style array.
      return [];
    }
  }
  return [];
};

export const getStyles = (config: any, extra: unknown): CytoscapeStyle => {
  const fontSize = Number(config?.style?.font?.size) || 12;
  const fontFamily = String(config?.style?.font?.name ?? 'sans-serif');
  const edgeArrow = Boolean(config?.style?.edge?.arrow);
  const edgeLabel = Boolean(config?.style?.edge?.label);

  const base: CytoscapeStyle = [
    {
      selector: 'node',
      style: {
        'background-color': 'data(backgroundColor)',
        width: 'data(width)',
        height: 'data(height)',
        color: 'data(color)',
        'border-color': 'data(borderColor)',
        'border-width': 'data(borderWidth)',
        'border-opacity': 0.5,
        content: 'data(label)',
        'text-valign': 'center',
        shape: (ele: cytoscape.NodeSingular) => ele.data('shape'),
        'text-wrap': 'wrap',
        'text-max-width': 'data(width)',
        'font-size': `${fontSize}px`,
        'font-family': fontFamily,
      },
    },
    { selector: 'node.label-hidden', style: { content: '' } },
    {
      selector: 'node.background-hidden',
      style: {
        width: 50,
        height: 50,
        shape: 'ellipse',
        'background-opacity': 0,
      },
    },
    { selector: 'node.border-hidden', style: { 'border-opacity': 0 } },
    {
      selector: 'edge',
      style: {
        width: 1,
        'line-color': 'data(lineColor)',
        'target-arrow-color': 'data(targetArrowColor)',
        'target-arrow-shape': (ele: cytoscape.EdgeSingular) => edgeArrow ? ele.data('targetArrowShape') : 'none',
        'curve-style': 'bezier',
        label: edgeLabel ? 'data(label)' : '',
      },
    },
    { selector: '.eh-handle', style: { 'border-width': 12 } },
    { selector: '.eh-source', style: { 'border-width': 2, 'border-color': 'red' } },
    { selector: '.eh-target', style: { 'border-width': 2, 'border-color': 'red' } },
    {
      selector: '.eh-preview, .eh-ghost-edge',
      style: {
        'background-color': 'red',
        'line-color': 'red',
        'target-arrow-color': 'red',
        'source-arrow-color': 'red',
      },
    },
    { selector: '.eh-ghost-edge.eh-preview-active', style: { opacity: 0 } },
    {
      selector: 'edge[label]',
      style: {
        'font-size': 8,
        'text-background-color': 'white',
        'text-background-opacity': 1,
        'text-background-padding': '2',
        'text-border-color': 'black',
        'text-border-style': 'solid',
        'text-border-width': 0.5,
        'text-border-opacity': 1,
      },
    },
    {
      selector: 'node.selected',
      style: { 'border-opacity': 0.7, 'border-color': '#e12b38', 'border-width': 3 },
    },
    {
      selector: 'node.selected.border-hidden',
      style: { 'border-opacity': 0, 'border-width': 0 },
    },
    {
      selector: 'edge.selected',
      style: {
        'line-opacity': 0.7,
        width: 1,
        'text-border-color': '#e12b38',
        'text-border-width': 1,
        'text-border-opacity': 0.7,
        'line-color': '#e12b38',
        'target-arrow-color': '#e12b38',
      },
    },
    {
      selector: 'node.hover',
      style: { 'border-opacity': 0.7, 'border-color': '#e12b38', 'border-width': 3 },
    },
    { selector: 'node.hover.border-hidden', style: { 'border-opacity': 0, 'border-width': 0 } },
    {
      selector: 'edge.hover',
      style: {
        'line-opacity': 0.7,
        width: 1,
        'text-border-color': '#e12b38',
        'text-border-width': 1,
        'text-border-opacity': 0.7,
        'line-color': '#e12b38',
        'target-arrow-color': '#e12b38',
      },
    },
    {
      selector: 'core',
      style: {
        'active-bg-color': '#ffffff',
        'active-bg-opacity': 0,
        'active-bg-size': 100,
        'selection-box-color': '#e12b38',
        'selection-box-border-color': '#e12b38',
        'selection-box-opacity': 0.2,
        'selection-box-border-width': 2,
        'outside-texture-bg-color': '#ffffff',
        'outside-texture-bg-opacity': 1,
      },
    },
  ];

  const extraArr = toArray(extra);
  return extraArr.length ? base.concat(extraArr) : base;
};
  
